import classNames from 'classnames';
import { CSSProperties, useMemo } from 'react';
import { Button } from 'components/atoms';
import { TbCheck } from 'react-icons/tb';
import { THEME_TYPE } from 'store/theme';
import './ThemeCard.scss';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useTheme } from 'hooks/useTheme';

interface IThemeCardProps {
  theme: THEME_TYPE;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}

const ThemeCard = ({ theme, selected = false, onClick, disabled = false }: IThemeCardProps) => {
  const { theme: currentTheme } = useTheme();

  const className = classNames('theme-card', `theme-card--${THEME_TYPE[theme]}`, {
    selected,
    disabled,
  });

  const previewStyle = useMemo(() => {
    const fallback = {
      [THEME_TYPE.light]: {
        background: '#f2f3f5',
        appBackground: '#dce1e8',
        text: '#162032',
        primary: '#4f90ff',
      },
      [THEME_TYPE.dark]: {
        background: '#242c37',
        appBackground: '#191d23',
        text: '#e4e4f2',
        primary: '#4f90ff',
      },
    };

    const colors =
      theme === THEME_TYPE.dark
        ? {
            background: currentTheme?.darkBackground ?? fallback[THEME_TYPE.dark].background,
            appBackground:
              currentTheme?.darkAppBackground ?? fallback[THEME_TYPE.dark].appBackground,
            text: currentTheme?.darkText ?? fallback[THEME_TYPE.dark].text,
            primary: currentTheme?.darkPrimary ?? fallback[THEME_TYPE.dark].primary,
          }
        : {
            background: currentTheme?.background ?? fallback[THEME_TYPE.light].background,
            appBackground: currentTheme?.appBackground ?? fallback[THEME_TYPE.light].appBackground,
            text: currentTheme?.text ?? fallback[THEME_TYPE.light].text,
            primary: currentTheme?.primary ?? fallback[THEME_TYPE.light].primary,
          };

    return {
      '--card-bg': colors.background,
      '--card-surface': colors.appBackground,
      '--card-text': colors.text,
      '--card-primary': colors.primary,
    } as CSSProperties;
  }, [theme, currentTheme]);

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      style={previewStyle}
    >
      <span className="theme-card__check">
        <Button onlyIcon size="compact" variant={BUTTON_VARIANT.submit}>
          <TbCheck />
        </Button>
      </span>

      <span className="theme-card__header" />
      <span className="theme-card__text" />
      <span className="theme-card__text theme-card__text--short" />
      <span className="theme-card__button" />
    </button>
  );
};

export default ThemeCard;
